import { useState, useEffect } from 'react';
import { Button, Modal } from '../../components/primitives';
import {
  usePoznamky,
  useCreatePoznamka,
  useUpdatePoznamka,
  useDeletePoznamka,
  useReorderPoznamky,
} from '../../api/hooks';
import type { PoznamkaKarta, PoznamkaBarva, PoznamkaPolozka } from '../../api/schemas';

/** Paleta barev kartiček (pozadí + okraj). */
const BARVY: Record<PoznamkaBarva, { bg: string; border: string }> = {
  default: { bg: '#ffffff', border: 'var(--sd-w-e2ddd0, #e2ddd0)' },
  zluta: { bg: '#fff8c5', border: '#efe08a' },
  zelena: { bg: '#e7f6ec', border: '#bfe0c8' },
  modra: { bg: '#e6f0fb', border: '#bcd6f0' },
  ruzova: { bg: '#fdecef', border: '#f3c6d0' },
  fialova: { bg: '#f1ecfb', border: '#d8c9f0' },
  seda: { bg: '#f1f0ec', border: 'var(--sd-w-dcd8cc, #dcd8cc)' },
};
const BARVY_KEYS = Object.keys(BARVY) as PoznamkaBarva[];

export function Nastenka() {
  const { data: karty = [], isLoading, error } = usePoznamky();
  const create = useCreatePoznamka();
  const update = useUpdatePoznamka();
  const del = useDeletePoznamka();
  const reorder = useReorderPoznamky();

  const [edit, setEdit] = useState<PoznamkaKarta | null>(null);

  // Lokální pořadí (kvůli plynulému drag & drop), synchronizované se serverem
  const [order, setOrder] = useState<PoznamkaKarta[]>(karty);
  const [dragId, setDragId] = useState<number | null>(null);
  useEffect(() => {
    if (dragId === null) setOrder(karty);
  }, [karty, dragId]);

  // Rychlé přidání
  const [adding, setAdding] = useState(false);
  const [nNadpis, setNNadpis] = useState('');
  const [nText, setNText] = useState('');
  const [nBarva, setNBarva] = useState<PoznamkaBarva>('default');

  async function handleAdd() {
    if (!nText.trim() && !nNadpis.trim()) {
      setAdding(false);
      return;
    }
    await create.mutateAsync({ nadpis: nNadpis.trim(), text: nText.trim(), barva: nBarva });
    setNNadpis(''); setNText(''); setNBarva('default'); setAdding(false);
  }

  function handleDragStart(e: React.DragEvent, id: number) {
    // Nezahajuj drag, když uživatel klikl na tlačítko/checkbox/text
    if ((e.target as HTMLElement).closest('button, input, textarea, a')) {
      e.preventDefault();
      return;
    }
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e: React.DragEvent, overId: number) {
    if (dragId === null || dragId === overId) return;
    e.preventDefault();
    setOrder((prev) => {
      const from = prev.findIndex((k) => k.id === dragId);
      const to = prev.findIndex((k) => k.id === overId);
      if (from === -1 || to === -1 || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  function handleDrop() {
    if (dragId !== null) reorder.mutate(order.map((k) => k.id));
    setDragId(null);
  }

  function toggleItem(k: PoznamkaKarta, idx: number) {
    const next = k.polozky.map((p, i) => (i === idx ? { ...p, hotovo: !p.hotovo } : p));
    update.mutate({ id: k.id, payload: { polozky: next } });
  }

  return (
    <>
      {/* Rychlé přidání — Keep styl */}
      <div style={{ maxWidth: 600, margin: '0 auto 22px' }}>
        <div style={{ border: '1px solid var(--sd-border, var(--sd-w-d8d4c5, #d8d4c5))', borderRadius: 8, background: BARVY[nBarva].bg, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: adding ? 12 : 8 }}>
          {!adding ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'text', fontSize: 14, color: 'var(--sd-text-dim)', padding: '6px 6px' }}
            >
              Vytvořit poznámku nebo úkol…
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                autoFocus
                value={nNadpis}
                onChange={(e) => setNNadpis(e.target.value)}
                placeholder="Nadpis"
                style={{ border: 'none', background: 'transparent', fontSize: 15, fontWeight: 700, outline: 'none', color: 'var(--sd-doc-ink)' }}
              />
              <textarea
                value={nText}
                onChange={(e) => setNText(e.target.value)}
                placeholder="Napište poznámku…"
                rows={3}
                style={{ border: 'none', background: 'transparent', fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: 'var(--sd-doc-ink)' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <BarvaPicker value={nBarva} onChange={setNBarva} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setNNadpis(''); setNText(''); setNBarva('default'); }}>Zrušit</Button>
                  <Button variant="primary" size="sm" onClick={handleAdd} disabled={create.isPending}>{create.isPending ? 'Ukládám…' : 'Přidat'}</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>Načítám…</div>
      ) : error ? (
        <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>Chyba načítání.</div>
      ) : order.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
          Zatím tu nic není. Přidejte si první poznámku nebo úkol nahoře.
        </div>
      ) : (
        <div style={{ columnWidth: 230, columnGap: 14 }}>
          {order.map((k) => (
            <Karta
              key={k.id}
              karta={k}
              dragging={dragId === k.id}
              onDragStart={(e) => handleDragStart(e, k.id)}
              onDragOver={(e) => handleDragOver(e, k.id)}
              onDrop={handleDrop}
              onDragEnd={handleDrop}
              onOpen={() => setEdit(k)}
              onToggleItem={(idx) => toggleItem(k, idx)}
              onTogglePin={() => update.mutate({ id: k.id, payload: { pripnuto: !k.pripnuto } })}
              onToggleDone={() => update.mutate({ id: k.id, payload: { hotovo: !k.hotovo } })}
              onDelete={() => del.mutate(k.id)}
            />
          ))}
        </div>
      )}

      <EditModal
        karta={edit}
        onClose={() => setEdit(null)}
        onSave={async (id, payload) => { await update.mutateAsync({ id, payload }); setEdit(null); }}
        onDelete={async (id) => { await del.mutateAsync(id); setEdit(null); }}
        saving={update.isPending}
        deleting={del.isPending}
      />
    </>
  );
}

function Karta({
  karta, dragging, onOpen, onToggleItem, onTogglePin, onToggleDone, onDelete,
  onDragStart, onDragOver, onDrop, onDragEnd,
}: {
  karta: PoznamkaKarta;
  dragging: boolean;
  onOpen: () => void;
  onToggleItem: (idx: number) => void;
  onTogglePin: () => void;
  onToggleDone: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const c = BARVY[karta.barva] ?? BARVY.default;
  const dim = karta.hotovo;
  const done = karta.polozky.filter((p) => p.hotovo).length;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        breakInside: 'avoid',
        marginBottom: 14,
        border: '1px solid',
        borderColor: c.border,
        borderRadius: 8,
        background: c.bg,
        boxShadow: dragging ? '0 6px 16px rgba(0,0,0,0.18)' : '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        opacity: dragging ? 0.5 : dim ? 0.6 : 1,
        cursor: 'grab',
      }}
    >
      <div onClick={onOpen} style={{ padding: '10px 12px 4px', cursor: 'pointer' }}>
        {karta.nadpis && (
          <div style={{ fontSize: 14.5, fontWeight: 700, marginBottom: karta.text || karta.polozky.length ? 4 : 0, color: 'var(--sd-doc-ink)', textDecoration: dim ? 'line-through' : 'none', wordBreak: 'break-word' }}>
            {karta.nadpis}
          </div>
        )}
        {karta.text && (
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--sd-doc-ink)', textDecoration: dim ? 'line-through' : 'none' }}>
            {karta.text}
          </div>
        )}
      </div>

      {karta.polozky.length > 0 && (
        <div style={{ padding: '2px 12px 6px' }}>
          {karta.polozky.map((p, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '2px 0', fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={p.hotovo}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => { e.stopPropagation(); onToggleItem(i); }}
                style={{ marginTop: 2 }}
              />
              <span style={{ textDecoration: p.hotovo ? 'line-through' : 'none', color: p.hotovo ? 'var(--sd-text-dim)' : 'var(--sd-doc-ink)', wordBreak: 'break-word' }}>{p.text}</span>
            </label>
          ))}
          <div style={{ fontSize: 11, color: 'var(--sd-text-dim)', marginTop: 2 }}>{done}/{karta.polozky.length} hotovo</div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '2px 6px 6px', justifyContent: 'flex-end' }}>
        <IconBtn title={karta.hotovo ? 'Označit jako nehotové' : 'Označit jako hotové'} active={karta.hotovo} onClick={onToggleDone}>✓</IconBtn>
        <IconBtn title={karta.pripnuto ? 'Odepnout' : 'Připnout nahoru'} active={karta.pripnuto} onClick={onTogglePin}>📌</IconBtn>
        <IconBtn title="Upravit" onClick={onOpen}>✎</IconBtn>
        <IconBtn title="Smazat" danger onClick={() => { if (window.confirm('Smazat tuto poznámku?')) onDelete(); }}>🗑</IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ children, title, onClick, active, danger }: { children: React.ReactNode; title: string; onClick: () => void; active?: boolean; danger?: boolean }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        background: active ? 'rgba(0,0,0,0.08)' : 'transparent',
        border: 'none',
        borderRadius: 6,
        width: 28,
        height: 26,
        cursor: 'pointer',
        fontSize: 13,
        lineHeight: 1,
        color: danger ? 'var(--sd-danger, #c0392b)' : 'var(--sd-text-dim)',
        opacity: active ? 1 : 0.75,
      }}
    >
      {children}
    </button>
  );
}

function BarvaPicker({ value, onChange }: { value: PoznamkaBarva; onChange: (b: PoznamkaBarva) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {BARVY_KEYS.map((b) => (
        <button
          key={b}
          type="button"
          onClick={() => onChange(b)}
          title={b}
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: BARVY[b].bg,
            border: value === b ? '2px solid var(--sd-doc-ink, #333)' : `1px solid ${BARVY[b].border}`,
            cursor: 'pointer',
          }}
        />
      ))}
    </div>
  );
}

function EditModal({
  karta, onClose, onSave, onDelete, saving, deleting,
}: {
  karta: PoznamkaKarta | null;
  onClose: () => void;
  onSave: (id: number, payload: { nadpis: string; text: string; barva: PoznamkaBarva; pripnuto: boolean; hotovo: boolean; polozky: PoznamkaPolozka[] }) => void;
  onDelete: (id: number) => void;
  saving: boolean;
  deleting: boolean;
}) {
  const [nadpis, setNadpis] = useState('');
  const [text, setText] = useState('');
  const [barva, setBarva] = useState<PoznamkaBarva>('default');
  const [pripnuto, setPripnuto] = useState(false);
  const [hotovo, setHotovo] = useState(false);
  const [polozky, setPolozky] = useState<PoznamkaPolozka[]>([]);
  const [loadedId, setLoadedId] = useState<number | null>(null);

  if (karta && karta.id !== loadedId) {
    setLoadedId(karta.id);
    setNadpis(karta.nadpis);
    setText(karta.text);
    setBarva(karta.barva);
    setPripnuto(karta.pripnuto);
    setHotovo(karta.hotovo);
    setPolozky(karta.polozky.map((p) => ({ ...p })));
  }

  if (!karta) return null;
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' } as const;

  function setItem(i: number, patch: Partial<PoznamkaPolozka>) {
    setPolozky((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }

  return (
    <Modal
      open={!!karta}
      onClose={onClose}
      title="Upravit poznámku"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Zavřít</Button>
          <Button variant="danger" onClick={() => { if (window.confirm('Smazat tuto poznámku?')) onDelete(karta.id); }} disabled={deleting}>{deleting ? 'Mažu…' : '🗑️ Smazat'}</Button>
          <Button variant="primary" onClick={() => onSave(karta.id, { nadpis: nadpis.trim(), text: text.trim(), barva, pripnuto, hotovo, polozky: polozky.filter((p) => p.text.trim()).map((p) => ({ text: p.text.trim(), hotovo: p.hotovo })) })} disabled={saving}>{saving ? 'Ukládám…' : 'Uložit'}</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, borderRadius: 8, background: BARVY[barva].bg, border: `1px solid ${BARVY[barva].border}` }}>
        <input value={nadpis} onChange={(e) => setNadpis(e.target.value)} placeholder="Nadpis" style={{ ...inp, fontWeight: 700, background: '#fff' }} />
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Text poznámky…" rows={4} style={{ ...inp, resize: 'vertical', background: '#fff' }} />

        {/* Checklist */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--sd-text-dim)', marginBottom: 6 }}>Zaškrtávací seznam</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {polozky.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={p.hotovo} onChange={(e) => setItem(i, { hotovo: e.target.checked })} />
                <input
                  value={p.text}
                  onChange={(e) => setItem(i, { text: e.target.value })}
                  placeholder="Položka…"
                  style={{ ...inp, padding: '5px 8px', background: '#fff', textDecoration: p.hotovo ? 'line-through' : 'none' }}
                />
                <button type="button" title="Odebrat položku" onClick={() => setPolozky((prev) => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sd-danger, #c0392b)', fontWeight: 700, fontSize: 16 }}>×</button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setPolozky((prev) => [...prev, { text: '', hotovo: false }])} style={{ marginTop: 8, background: 'none', border: '1px dashed var(--sd-border, var(--sd-w-d8d4c5, #d8d4c5))', borderRadius: 6, padding: '5px 10px', fontSize: 13, cursor: 'pointer', color: 'var(--sd-text-dim)' }}>+ přidat položku</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <BarvaPicker value={barva} onChange={setBarva} />
          <div style={{ display: 'flex', gap: 14 }}>
            <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={pripnuto} onChange={(e) => setPripnuto(e.target.checked)} /> Připnout
            </label>
            <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={hotovo} onChange={(e) => setHotovo(e.target.checked)} /> Hotovo
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
}
